<template>
    <div class="EntityDictsDetail">
        <WCard>
            <div class="flex row-stretch">
                <div class="Values">
                    <div class="flex row-stretch-center mb-1">
                        <WText
                            tag="span"
                            weight="semi"
                            color="gray02"
                            uppercase
                            size="14"
                        >
                            {{ $t('entities.dicts.items') }}
                        </WText>
                        <div class="Actions">
                            <WButton
                                iconLeft="save"
                                :iconSize="18"
                                outline
                                color="accent-blue"
                                size="sm"
                                class="mr-1"
                                @click="handleSaveValuesOrder"
                            >
                                {{ $t('schema.saveOrder') }}
                            </WButton>
                            <WButton
                                iconLeft="plus-circle"
                                :iconSize="18"
                                size="sm"
                                @click="handleRequest(modals.NEW_VALUE)"
                            >
                                {{ $t(`entities.dicts.newItem`) }}
                            </WButton>
                        </div>
                    </div>
                    <DataGrid
                        ref="values"
                        class="DataGrid"
                        :loading="isFetching"
                        v-bind="normalizeValues"
                        @onCellChanged="handleCellChanged"
                    />
                </div>
                <div class="flex-1 ml-1">
                    <div class="flex row-stretch-center mb-1">
                        <WText
                            tag="span"
                            weight="semi"
                            color="gray02"
                            uppercase
                            size="14"
                        >
                            {{ $t('entities.dicts.relations') }}
                        </WText>
                        <div class="Actions">
                            <WButton
                                iconLeft="save"
                                :iconSize="18"
                                outline
                                color="accent-blue"
                                size="sm"
                                class="mr-1"
                                @click="handleSaveMapsOrder"
                            >
                                {{ $t('schema.saveOrder') }}
                            </WButton>
                            <WButton
                                iconLeft="plus-circle"
                                :iconSize="18"
                                size="sm"
                                @click="handleRequest(modals.NEW_MAP)"
                            >
                                {{ $t(`entities.dicts.newRelationship`) }}
                            </WButton>
                        </div>
                    </div>
                    <DataGrid
                        ref="maps"
                        class="DataGrid"
                        :loading="isFetching"
                        v-bind="normalizeMaps"
                        @onCellChanged="handleCellChanged"
                    />
                </div>
            </div>
        </WCard>

        <Modal
            :opened="modal === modals.NEW_MAP"
            :title="$t(`entities.dicts.newRelationship`)"
            class="ModalSelect"
            #body
            @close="handleCleanModal"
        >
            <MapForm
                :dictKey="dictKey"
                :options="normalizeOptions"
                @onReset="handleCleanModal"
                @onSubmit="handleSubmitItem"
            />
        </Modal>

        <Modal
            :opened="modal === modals.NEW_VALUE"
            :title="$t(`entities.dicts.newItem`)"
            class="ModalSelect"
            #body
            @close="handleCleanModal"
        >
            <ValueForm
                :dictKey="dictKey"
                @onReset="handleCleanModal"
                @onSubmit="handleSubmitItem"
            />
        </Modal>

        <ModalRequest
            :opened="modal === modals.SAVE_ORDER"
            :title="$t('schema.modals.saveOrder.title')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="saveOrder"
        />

        <ModalRequest
            :opened="modal === modals.REMOVE_DICT"
            :title="$t('entities.modals.removeDict.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="removeDict"
        />

        <ModalRequest
            :opened="modal === modals.REMOVE"
            :title="$t('entities.modals.removeProp.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="removeField"
        />

        <ModalRequest
            :opened="modal === modals.EDIT_SELECTION"
            :title="$t('entities.actionsModals.editSelection.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            preventDefault
            @close="handleCancelEdition"
            @onAccept="updateField"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, rethrowError, notifySuccess, notifyError, notifyHttpError, sleep, task,
} from '@/api'
import GridEntity from '@/entities/grid'
import { DataGrid, ModalRequest, Modal } from '@/components'
import { entityMixin } from '@/mixins'
import { MapForm, ValueForm } from './components'

const GRIDS = {
    VALUES: 'values',
    MAPS: 'maps',
}

const COLS = {
    [GRIDS.VALUES]: [
        {
            headerName: 'Display Items',
            colId: 'label',
            field: 'label',
            editable: true,
        },
    ],
    [GRIDS.MAPS]: [
        {
            headerName: 'Input Data',
            field: 'alias',
            colId: 'alias',
            editable: true,
        },
        {
            headerName: 'Display Data',
            field: 'value',
            colId: 'value',
            editable: true,
            type: 'select',
            selectGroup: 'values',
        },
    ],
}

const MODALS = {
    EDIT: 'EDIT',
    REMOVE: 'REMOVE',
    NEW_MAP: 'NEW_MAP',
    NEW_VALUE: 'NEW_VALUE',

    REMOVE_DICT: 'REMOVE_DICT',
    EDIT_SELECTION: 'EDIT_SELECTION',

    SAVE_ORDER: 'SAVE_ORDER',
}

const DATAGRID_CONFIG = {
    hasInputFilter: false,
    filterable: false,
    sideBar: false,
    cleanFiltersButton: false,
    fitResponsive: true,
    ...GridEntity.draggableConfig,
}

export default {
    name: 'EntityDictsDetail',
    components: {
        DataGrid,
        ModalRequest,
        Modal,
        MapForm,
        ValueForm,
    },
    mixins: [entityMixin],
    props: {
        entityKey: VueTypes.string,
    },
    entity () {
        const { dictKey } = this.$route.params || {}

        return {
            breadcrumbs: [
                {
                    key: 'Dicts',
                    label: this.$route.meta.breadcrumb,
                    to: { name: 'Dicts' },
                }, {
                    key: 'DictsDetail',
                    label: dictKey,
                    to: { name: 'DictsDetail', params: { dictKey } },
                }],
        }
    },
    data () {
        return {
            dictKey: undefined,
            activeGrid: undefined,
            data: undefined,
            isFetching: false,

            modals: MODALS,
            modal: undefined,

            node: undefined,
            isLoading: false,

            // Edit Cell request
            editionData: undefined,
            isEditingCell: false,
        }
    },
    computed: {
        normalizeValues () {
            const { values: rows = [] } = this.data || {}
            return {
                ...this.dataGridConfig,
                columns: COLS[GRIDS.VALUES],
                rows: rows.map(row => ({ ...row, fieldType: GRIDS.VALUES })),
            }
        },
        normalizeMaps () {
            const { maps: rows = [], values = [] } = this.data || {}
            const selectGroups = {
                values: values.reduce((acc, { id, label }) => ({ ...acc, [id]: label }), {}),
            }

            return {
                ...this.dataGridConfig,
                columns: COLS[GRIDS.MAPS],
                rows: rows.map(row => ({ ...row, fieldType: GRIDS.MAPS, value: row.value || '' })),
                selectGroups,
            }
        },
        normalizeOptions () {
            const { values: options = [] } = this.data || {}
            return options.map(({ id, label }) => ({ key: id, label }))
        },
    },
    beforeMount () {
        this.dataGridConfig = {
            ...DATAGRID_CONFIG,
            contextMenu: this.contextMenu,
        }
    },
    mounted () {
        const { dictKey } = this.$route.params || {}
        this.dictKey = dictKey

        if (dictKey) this.getData()
    },
    methods: {
        contextMenu ({ node, api }) {
            const renderIcon = icon => `<i class="WIcon icon-${icon} ag-icon" />`

            return [
                'copy',
                'paste',
                'separator',
                ...([
                    {
                        name: this.$t('entities.actions.delete'),
                        action: () => this.handleRequest(this.modals.REMOVE, node),
                        icon: renderIcon('trash'),
                    },
                ]),
                'separator',
                {
                    name: this.$t('entities.actions.export'),
                    action: () => api.exportDataAsCsv({ columnSeparator: '\t' }),
                    icon: renderIcon('download'),
                },
            ]
        },
        enableButtons () {
            const buttons = [{
                iconLeft: 'trash',
                text: this.$t('entities.removeDict'),
                onClick: () => this.handleRequest(MODALS.REMOVE_DICT),
            }]
            this.setEntity({ buttons })
        },
        handleSubmitItem () {
            notifySuccess(this.$t('entities.modals.createItemDict'))()
            this.handleCleanModal()
            this.getData()
        },
        /*
        * Modals
        */
        handleCleanModal () {
            this.modal = undefined
            this.node = undefined
            this.editionData = undefined
        },
        handleRequest (modal, node) {
            this.node = node
            if (modal) this.modal = modal
        },
        handleSaveValuesOrder () {
            this.activeGrid = GRIDS.VALUES
            this.handleRequest(MODALS.SAVE_ORDER)
        },
        handleSaveMapsOrder () {
            this.activeGrid = GRIDS.MAPS
            this.handleRequest(MODALS.SAVE_ORDER)
        },
        /*
        * Cells
        */
        handleCellChanged ({
            node, colDef, data, oldValue, newValue,
        }) {
            if (GridEntity.compareValues(newValue, oldValue, colDef.type) && !this.isEditingCell) {
                this.editionData = {
                    fieldKey: colDef.field, row: data, oldValue, newValue,
                }
                this.handleRequest(this.modals.EDIT_SELECTION, node)
                this.isEditingCell = true
            } else {
                this.isEditingCell = false // End of field edit
            }
        },
        handleCancelEdition () {
            this.node.setDataValue(this.editionData.fieldKey, this.editionData.oldValue)
            this.handleCleanModal()
        },
        /**
         * Requests
         */
        async getData () {
            this.isFetching = true
            apiRequest(Api().dictionaries.get({ dictKey: this.dictKey }))
                .then(({ data }) => {
                    data.maps = data.maps.map((m) => {
                        m.value = data.values.find(v => v.id === m.value)?.label
                        return m
                    })
                    this.data = data
                    if (!data.locked) this.enableButtons()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isFetching = false))
        },
        async removeDict () {
            this.isLoading = true
            const params = { dictKey: this.dictKey }
            apiRequest(Api().dictionaries.delete(params))
                .then(task(this.handleCleanModal))
                .then(sleep(600))
                .then(task(() => this.$router.back()))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        async createField (params) {
            this.isLoading = true
            apiRequest(Api().dictionaries.fieldCreate(params))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        async updateField () {
            this.isLoading = true
            const { fieldKey, row, newValue: value } = this.editionData
            const { id: fieldId, fieldType, ...data } = row
            const params = {
                dictKey: this.dictKey,
                fieldType,
                fieldId,
                params: { ...data, [fieldKey]: value },
            }

            params.params.value = fieldId
            apiRequest(Api().dictionaries.fieldEdit(params))
                .then(() => {
                    notifySuccess(this.$t('entities.modals.editSuccess'))()
                    this.handleCleanModal()
                    this.getData()
                    this.isEditingCell = false
                    this.isLoading = false
                })
                .catch((error) => {
                    this.handleCancelEdition()
                    rethrowError(notifyHttpError())(error)
                })
                .finally(() => (this.isLoading = false))
        },
        async removeField () {
            this.isLoading = true
            const { id: fieldId, fieldType } = (this.node || {}).data
            const params = { dictKey: this.dictKey, fieldType, fieldId }
            apiRequest(Api().dictionaries.fieldDelete(params))
                .then(task(() => {
                    if (this.$refs[fieldType]) {
                        this.$refs[fieldType].removeRow(this.node)
                    }
                }))
                .then(task(this.handleCleanModal))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        async saveOrder () {
            this.isLoading = true
            const { gridApi } = this.$refs[this.activeGrid] || {}
            const order = GridEntity.getFieldNodes(gridApi, 'id')
            const params = {
                dictKey: this.dictKey,
                fieldType: this.activeGrid,
                params: { order },
            }
            apiRequest(Api().dictionaries.order(params))
                .then(() => notifySuccess(this.$t('schema.modals.saveOrderSuccess'))())
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.activeGrid = undefined
                    this.handleCleanModal()
                    this.isLoading = false
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.DataGrid {
	@include admin-screen-height(122px);
}

.Values {
    min-width: calc(33% - 16px);
}

.ModalSelect {
    &::v-deep .Dialog {
        overflow: initial;
    }
}

.WButton {
    &::v-deep .Wrapper span {
        display: block;
        margin-top: -2px;
    }
}
</style>
